import * as React from 'react';
// @ts-expect-error
import ReactTooltip from 'react-tooltip';

import {SeeAllButton} from '../button/see-all-button/see-all-button';

import {AddNewCard} from './add-new-card';
import {EditionThumbnailCard} from './edition-thumbnail-card';
import type {Edition} from './types';

import styles from './edition-thumbnail-list.module.css';

// Thumbnail width + 16px of padding
const ITEM_PADDING = 16;
const ITEM_WIDTH = 300 + ITEM_PADDING;
const ITEM_HEIGHT = 290;

// Id for the 'Add new' card option
export const NEW_EDITION_ID = 'CREATE_FROM_SCRATCH';

type Props = {
    editions: Edition[];
    isLoading?: boolean;
    selectedId?: string | null | undefined;
    onSelect?: (editionId: string) => void;
    seeAll?: {
        text: string;
        onClick: () => void;
    };
    addNew?: {
        text: string;
        onClick: () => void;
        isSelectable?: boolean;
    };
    singleRow?: boolean;
    displayIcon?: boolean;
    shouldTruncateName?: boolean;
    // Defaults to white as this should most often appear on an off-white background
    loadingStateBackgroundColor?: 'white' | 'off-white';
};

export function EditionThumbnailList(props: Props) {
    const {loadingStateBackgroundColor = 'white'} = props;

    const thumbnailListRef = React.useRef(null);
    const [listWidth, setListWidth] = React.useState<number | null | undefined>(undefined);

    function updateRefMeasurements() {
        if (thumbnailListRef.current) {
            // @ts-expect-error
            setListWidth(thumbnailListRef.current.clientWidth);
        }
    }

    React.useEffect(() => {
        updateRefMeasurements();
        window.addEventListener('resize', updateRefMeasurements);

        return () => window.removeEventListener('resize', updateRefMeasurements);
    }, []);

    React.useEffect(() => {
        updateRefMeasurements();
    }, [props.editions]);

    const thumbnailsToShow = getNumThumbnailsToShow(
        listWidth,
        props.editions.length,
        Boolean(props.addNew),
        Boolean(props.singleRow)
    );

    return (
        <>
            <div
                ref={thumbnailListRef}
                className={
                    props.singleRow
                        ? styles['thumbnail-preview-list--single-row']
                        : styles['thumbnail-preview-list']
                }
            >
                {props.addNew ? (
                    <AddNewCard
                        text={props.addNew.text}
                        onClick={props.addNew.onClick}
                        isLoading={props.isLoading}
                        isSelected={props.selectedId === NEW_EDITION_ID}
                        isSelectable={props.addNew.isSelectable}
                        height={ITEM_HEIGHT}
                    />
                ) : undefined}
                {props.editions.length
                    ? props.editions
                          .slice(0, thumbnailsToShow)
                          .map((edition) => (
                              <EditionThumbnailCard
                                  key={edition.id}
                                  edition={edition}
                                  selectedId={props.selectedId}
                                  displayIcon={props.displayIcon}
                                  onSelect={props.onSelect}
                                  shouldTruncateName={props.shouldTruncateName}
                              />
                          ))
                    : [...Array(thumbnailsToShow)].map((_, index) => (
                          <div
                              key={`placeholder-${index}`}
                              className={
                                  loadingStateBackgroundColor === 'white'
                                      ? styles['placeholder--white-background']
                                      : styles.placeholder
                              }
                          />
                      ))}
                {props.seeAll && thumbnailsToShow < props.editions.length ? (
                    <div className={styles['see-all-button']}>
                        <SeeAllButton text={props.seeAll.text} onClick={props.seeAll.onClick} />
                    </div>
                ) : undefined}
            </div>
            <ReactTooltip
                delayShow={300}
                className='react-tooltip'
                effect='solid'
                multiline={false}
                id='icon-tip'
            />
        </>
    );
}

function getNumThumbnailsToShow(
    listWidth: number | null | undefined,
    numEditions: number,
    addNew: boolean,
    singleRow: boolean
): number {
    // Show at minimum 1
    let thumbnailsToShow = 1;

    if (!listWidth) {
        return thumbnailsToShow;
    }

    if (singleRow) {
        if (numEditions) {
            // Number of items shown, including add new button if there is one
            const numItems = addNew ? numEditions + 1 : numEditions;
            // Space needed to show all items minus 16px padding not on last element
            const spaceNeeded = ITEM_WIDTH * numItems - ITEM_PADDING;

            if (listWidth - spaceNeeded < 0) {
                // Need to have room (126px) for the 'see all' button
                const maxThumbnails = Math.floor((listWidth - 126) / ITEM_WIDTH);

                // Thumbnails shown offset by 1 if there is an add new button
                thumbnailsToShow = addNew ? maxThumbnails - 1 : maxThumbnails;
            } else {
                // We have room to show everything
                thumbnailsToShow = numEditions;
            }
        } else {
            thumbnailsToShow = Math.floor(listWidth / ITEM_WIDTH);
            if (addNew) {
                thumbnailsToShow = thumbnailsToShow - 1;
            }
        }
    } else if (numEditions) {
        thumbnailsToShow = numEditions;
    } else {
        thumbnailsToShow = 8;

        if (addNew) {
            thumbnailsToShow = thumbnailsToShow - 1;
        }
    }

    return thumbnailsToShow;
}
